import SEOHead from "@/components/SEOHead";
import { Overline } from "@/components/Section";
import { Link } from "react-router-dom";

const SECTIONS = [
  { id: "overview",       label: "Overview" },
  { id: "no-refunds",     label: "General No-Refund Policy" },
  { id: "replacement",    label: "Replacement Credit (Section 3B)" },
  { id: "weekly-flex",    label: "Weekly Flex Billing Cycle" },
  { id: "standard",       label: "Standard 50/50 Plan" },
  { id: "one-time",       label: "One-Time Plan" },
  { id: "late-fees",      label: "Late Fees & Default" },
  { id: "contact",        label: "Contact" },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 mb-12 pb-12 border-b border-border last:border-0 last:mb-0 last:pb-0">
      <h2 className="font-head text-xl md:text-2xl font-bold uppercase tracking-[0.12em] text-[#0A0A0A] mb-4">{title}</h2>
      <div className="space-y-4 text-foreground/75 text-base leading-relaxed">{children}</div>
    </section>
  );
}

function Highlight({ children }) {
  return (
    <div className="border-l-4 border-primary bg-primary/5 px-5 py-4 text-sm leading-relaxed text-foreground/80">
      {children}
    </div>
  );
}

export default function RefundPolicy() {
  return (
    <>
      <SEOHead
        title="Refund Policy | Hylas Companion"
        description="Refund and payment policy for Hylas Companion carrier placement fees — Weekly Flex billing cycle, Replacement Credits, and general no-refund terms per the Fleet Client Agreement."
        path="/refund-policy"
      />

      {/* Hero */}
      <section className="bg-[#0A0A0A] pt-40 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Overline light>Legal</Overline>
          <h1 className="font-head text-5xl sm:text-6xl font-bold uppercase tracking-[0.12em] text-white mt-5 leading-none">
            Refund<br /><span className="text-primary">Policy</span>
          </h1>
          <p className="mt-6 text-white/50 text-sm">
            Effective Date: February 20, 2026 &nbsp;·&nbsp; Applies to: Fleet Client Agreement — Andrej Sasaroga DBA Hylas Companion
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-16 xl:gap-24">

          {/* Sticky TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <p className="font-head text-[10px] uppercase tracking-[0.35em] text-primary mb-4">Contents</p>
              <ul className="space-y-2.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="text-sm text-foreground/50 hover:text-primary transition-colors font-head uppercase tracking-[0.1em]">
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Body */}
          <div>
            <Section id="overview" title="Overview">
              <p>
                This Refund Policy governs all placement fees paid to <strong>Andrej Sasaroga DBA Hylas Companion</strong> ("Hylas") under the Fleet Client Agreement. By signing that agreement, the carrier ("Client") agrees to the terms described here.
              </p>
              <p>
                Hylas is a driver recruiting and marketing service only — not a motor carrier, employer, or staffing agency. Placement fees compensate Hylas for the referral and lead-generation service, which is fully rendered upon driver submission and first dispatch regardless of subsequent employment duration.
              </p>
            </Section>

            <Section id="no-refunds" title="General No-Refund Policy">
              <p>
                <strong>All placement fees are non-refundable.</strong> Once a driver has been dispatched for the first time under Client's authority, the full placement fee obligation is earned and due regardless of:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-sm">
                <li>How long the driver stays with the company</li>
                <li>Whether the driver passes or fails DOT qualification, drug testing, or background checks</li>
                <li>Whether the driver performs satisfactorily</li>
                <li>Any dispute between Client and the driver after placement</li>
                <li>Business decisions, downsizing, or closure of Client's operation</li>
              </ul>
              <Highlight>
                There are no cash refunds under any payment plan. The sole exception is the Replacement Credit described in Section 3B of the Fleet Client Agreement, which provides a non-cash credit — not a cash refund — under specific, documented conditions.
              </Highlight>
            </Section>

            <Section id="replacement" title="Replacement Credit (Section 3B)">
              <p>
                A one-time placement credit may be issued under the following conditions:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-sm">
                <li>The driver quits, abandons, or is terminated for cause within <strong>7 calendar days</strong> of first dispatch.</li>
                <li>Client is in documented net loss — expenses exceed revenue for that driver during those 7 days.</li>
                <li>Client notifies Hylas within <strong>48 hours</strong> of the driver's departure.</li>
                <li>Client provides settlement sheets or expense documentation proving net loss.</li>
              </ul>
              <p className="text-sm font-medium text-foreground/90 mt-2">Credit conditions:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm">
                <li>Credit equals the documented net loss amount, capped at the amount actually paid to Hylas for that driver.</li>
                <li>Credit applies <strong>only to the next driver placement fee</strong> — it is not cash, not transferable, and not refundable.</li>
                <li>Credit expires after <strong>90 days</strong> from date of issue. No extensions.</li>
                <li>One credit per failed placement. Credits cannot be stacked.</li>
              </ul>
              <Highlight>
                <strong>Replacement Credit applies only to the Standard 50/50 and One-Time payment plans.</strong> The Weekly Flex plan is not eligible for Replacement Credit under any circumstances.
              </Highlight>
            </Section>

            <Section id="weekly-flex" title="Weekly Flex Billing Cycle">
              <p>
                The Weekly Flex plan charges a variable weekly fee based on position type, pay program, and selected duration (9 / 17 / 35 / 52 weeks). <strong>4-week minimum commitment applies to all plans.</strong> Floor rates: Semi Truck from $95/wk (52-wk) to $375/wk (9-wk). Box Truck from $55/wk (52-wk) to $250/wk (9-wk). No maximum cap — carrier bids higher to win.
              </p>
              <p className="font-semibold text-foreground/90">Billing cycle: Monday 12:00am to Sunday 11:59pm CST</p>
              <p className="text-sm">
                Each billing week runs from <strong>Monday through Sunday</strong>. A payment processed on Monday covers that entire calendar week (Monday–Sunday).
              </p>

              <div className="rounded-md overflow-hidden border border-border text-sm">
                <div className="bg-[#0A0A0A] text-white px-4 py-2.5 font-head text-[10px] uppercase tracking-[0.2em]">
                  Weekly Flex — What happens if the driver leaves mid-week?
                </div>
                <div className="divide-y divide-border">
                  <div className="grid grid-cols-[1fr_1fr] px-4 py-3 bg-muted/30">
                    <span className="font-semibold text-xs uppercase tracking-wide text-foreground/50">Scenario</span>
                    <span className="font-semibold text-xs uppercase tracking-wide text-foreground/50">Billing outcome</span>
                  </div>
                  {[
                    ["Driver leaves on Monday (same day as payment)", "Current week is non-refundable. Next Monday charge does not occur."],
                    ["Driver leaves Tuesday through Sunday", "Current week is non-refundable. Subscription stops — no charge on the following Monday."],
                    ["Driver completes all selected weeks", "Full contracted amount paid. No further charges."],
                  ].map(([scenario, outcome], i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr] px-4 py-3">
                      <span className="text-foreground/70 pr-4">{scenario}</span>
                      <span className="text-foreground/90">{outcome}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Highlight>
                When a driver leaves the company, the Weekly Flex subscription stops immediately and automatically. Hylas will not charge for the following billing period. However, the payment already made for the current week (Monday–Sunday) is non-refundable, regardless of which day within that week the driver departed. Client must notify Hylas within 24 hours of separation via email to <strong>support@hylascompanion.us</strong>; failure to notify results in the following week being billable. If any weekly payment is 7+ days overdue, Hylas has immediate remarketing rights (see Late Fees & Default section).
              </Highlight>

              <p className="text-sm">
                <strong>No Replacement Credit</strong> is available for the Weekly Flex plan. Credits apply only to Standard 50/50 and One-Time plans.
              </p>
            </Section>

            <Section id="standard" title="Standard 50/50 Plan">
              <p>
                The Standard 50/50 plan requires two payments per driver:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-sm">
                <li><strong>Payment 1 (Day 0 — First Dispatch):</strong> 50% of winning bid due on Driver Hire Day.</li>
                <li><strong>Payment 2 (Day 14):</strong> Remaining 50% due 14 days after First Dispatch.</li>
              </ul>
              <p className="text-sm">
                Neither payment is refundable after the due date. If the driver leaves between Payment 1 and Payment 2, Payment 2 remains due and collectible. The Replacement Credit under Section 3B may apply if the driver leaves within the first 7 days and all credit conditions are met. If either payment is more than 7 days overdue, Hylas has the right to immediately re-market the driver.
              </p>
            </Section>

            <Section id="one-time" title="One-Time Plan">
              <p>
                The One-Time plan requires a single payment of 100% of the winning bid on Driver Hire Day (First Dispatch). This includes a <strong>$100–$600 discount</strong> vs. Standard 50/50, depending on truck type and bid amount.
              </p>
              <p className="text-sm">
                This payment is fully earned and non-refundable upon First Dispatch. The Replacement Credit under Section 3B may apply if the driver leaves within the first 7 days and all credit conditions are met.
              </p>
            </Section>

            <Section id="late-fees" title="Late Fees & Default">
              <p>
                Unpaid placement fees accrue late fees as liquidated damages: <strong>$300/day per Semi Truck placement</strong> and <strong>$150/day per Box Truck placement</strong>, capped at 100% of the original Placement Fee per driver. An additional <strong>1.5% monthly interest</strong> applies after 30 days overdue on the total outstanding balance.
              </p>
              <p className="text-sm">
                Hylas reserves the right to suspend all new driver transmissions and pause active bids while any fee remains outstanding over 7 days.
              </p>
              <p className="text-sm font-semibold text-foreground/90">
                Immediate Remarketing Rights: If any payment is 7 or more days overdue — even while the driver is still dispatched with the Client — Hylas has the immediate right to return the driver to Hylas Market and re-market, transmit, and place that driver with another fleet. Client remains liable for original fee plus all accrued late fees and interest, and shall not interfere with Hylas re-marketing efforts. This applies to all payment plans including Weekly Flex (each Monday is a due date).
              </p>
            </Section>

            <Section id="contact" title="Contact">
              <p>
                For questions about this policy, billing disputes, or to submit a Replacement Credit claim, contact:
              </p>
              <p className="text-sm">
                <strong>Andrej Sasaroga DBA Hylas Companion</strong><br />
                Email: <a href="mailto:contact@hylascompanion.com" className="text-primary hover:underline">contact@hylascompanion.com</a><br />
                Phone: <a href="tel:+17083959565" className="text-primary hover:underline">(708) 395-9565</a>
              </p>
              <p className="text-sm">
                This policy is incorporated by reference into the Fleet Client Agreement. In case of conflict between this page and the signed Fleet Client Agreement, the signed agreement controls.
              </p>
              <p className="text-sm">
                For the full agreement text, see the{" "}
                <Link to="/fleet" className="text-primary hover:underline">Fleet page</Link>{" "}
                or contact us directly.
              </p>
            </Section>
          </div>
        </div>
      </div>
    </>
  );
}
